import { WORLD_WIDTH, WORLD_HEIGHT, BORDER_THICKNESS } from './constants.js';

export interface ArenaObstacle {
  x: number;
  y: number;
  /** Collision radius in pixels (physics + hit detection). */
  radius: number;
  /**
   * Phaser atlas key.
   * 'sheet' = space-shooter-remastered  |  'ext' = space-shooter-extension
   */
  texture: string;
  /** Atlas frame name for rendering. */
  frame: string;
  /** Render scale applied to the sprite. */
  scale: number;
  /** Initial rotation in radians. */
  rotation: number;
}

export interface ArenaConfig {
  name: string;
  /**
   * Normalised obstacle density in [0, 1].
   *   0    = empty arena
   *   0.25 = sparse (≈12 obstacles) — default for "The Void"
   *   0.5  = moderate (≈25 obstacles)
   *   1.0  = dense (≈50 obstacles)
   */
  obstacleDensity: number;
  /** RNG seed — same seed produces identical layouts on server and client. */
  seed: number;
  /** Seconds between powerup spawn attempts. Defaults to 8 if omitted. */
  powerupDropFrequency?: number;
}

export const ARENAS: Record<string, ArenaConfig> = {
  'The Void': {
    name: 'The Void',
    obstacleDensity: 0.25,
    seed: 0xcafebabe,
    powerupDropFrequency: 8,
  },
};

// ---------------------------------------------------------------------------
// Individual obstacle specs
// ---------------------------------------------------------------------------

/**
 * [texture, frame, renderScale, collisionRadius]
 *
 * Radii are conservative vs. sprite bounds so ships can pass close without
 * phantom hits.
 */
const OBSTACLE_SPECS: [string, string, number, number][] = [
  // Original remastered — large asteroids
  ['sheet', 'meteorBrown_big1.png', 1.0, 44],
  ['sheet', 'meteorBrown_big2.png', 1.0, 50],
  ['sheet', 'meteorBrown_big3.png', 1.0, 42],
  ['sheet', 'meteorBrown_big4.png', 1.0, 46],
  ['sheet', 'meteorGrey_big1.png',  1.0, 44],
  ['sheet', 'meteorGrey_big2.png',  1.0, 50],
  ['sheet', 'meteorGrey_big3.png',  1.0, 42],
  ['sheet', 'meteorGrey_big4.png',  1.0, 46],
  // Original remastered — medium asteroids
  ['sheet', 'meteorBrown_med1.png', 1.0, 20],
  ['sheet', 'meteorBrown_med3.png', 1.0, 19],
  ['sheet', 'meteorGrey_med1.png',  1.0, 20],
  ['sheet', 'meteorGrey_med2.png',  1.0, 19],
  // Extension — giant meteors (~215 px native, scaled to ~160 px display)
  ['ext', 'spaceMeteors_001.png', 0.75, 72],
  ['ext', 'spaceMeteors_002.png', 0.75, 72],
  ['ext', 'spaceMeteors_003.png', 0.75, 72],
  ['ext', 'spaceMeteors_004.png', 0.75, 72],
  // Extension — space stations
  ['ext', 'spaceStation_024.png', 0.45, 58],
  ['ext', 'spaceStation_021.png', 0.50, 48],
  // Extension — buildings
  ['ext', 'spaceBuilding_016.png', 1.0, 36],
  ['ext', 'spaceBuilding_007.png', 1.0, 34],
  ['ext', 'spaceBuilding_009.png', 1.0, 34],
];

const WEIGHTS = [
  3, 3, 3, 3, 3, 3, 3, 3,  // original big  (8)
  2, 2, 2, 2,               // original med  (4)
  5, 5, 5, 5,               // giant meteors (4)
  2, 2,                     // stations      (2)
  2, 2, 2,                  // buildings     (3)
];
const TOTAL_WEIGHT = WEIGHTS.reduce((s, w) => s + w, 0);

/** Minimum clear gap (px) between edges of any two SEPARATELY-placed obstacles. */
const MIN_GAP = 90;

// ---------------------------------------------------------------------------
// Formation templates
// ---------------------------------------------------------------------------

/**
 * A single asteroid within a formation.
 * dx/dy are offsets from the formation's centre point (pre-rotation).
 * large = true → LARGE_RADIUS; false → MED_RADIUS.
 */
interface FormationSlot { dx: number; dy: number; large: boolean; }

/**
 * Collision radii used for formation slot physics.
 * Match the average of the frame pools below.
 */
const LARGE_RADIUS = 48;
const MED_RADIUS   = 20;

const LARGE_FRAMES = [
  'meteorBrown_big1.png', 'meteorBrown_big2.png',
  'meteorBrown_big3.png', 'meteorBrown_big4.png',
  'meteorGrey_big1.png',  'meteorGrey_big2.png',
  'meteorGrey_big3.png',  'meteorGrey_big4.png',
];
const MED_FRAMES = [
  'meteorBrown_med1.png', 'meteorBrown_med3.png',
  'meteorGrey_med1.png',  'meteorGrey_med2.png',
];

/**
 * Spacing guide (large asteroid radius = 48 px):
 *   r+r+gap = center-to-center distance
 *   96 + 100 = 196 px  → ~100 px passage  (one ship, tight)
 *   96 +  40 = 136 px  → ~40  px gap      (impassable wall)
 *   96 +   4 = 100 px  → ~4   px gap      (sealed)
 *
 * All formations use only 'sheet' atlas asteroids; extension pack assets
 * appear only as individual scattered obstacles.
 *
 * Each formation is randomly rotated at placement time so it can appear
 * at any angle in the arena.
 */
const FORMATION_TEMPLATES: FormationSlot[][] = [
  // ── Gate ─────────────────────────────────────────────────────────────────
  // Two boulders either side of a ~100 px passage.
  // Simple chokepoint that rewards positional play.
  [
    { dx: -98, dy: 0, large: true },
    { dx:  98, dy: 0, large: true },
  ],

  // ── L-bracket ────────────────────────────────────────────────────────────
  // Three-boulder vertical arm with one step at the base.
  // Classic corner-ambush cover; rotate for L / J / Γ / ⌐ orientations.
  [
    { dx:   0, dy:    0, large: true },
    { dx:   0, dy: -192, large: true },
    { dx:   0, dy: -384, large: true },
    { dx: 192, dy:    0, large: true },
  ],

  // ── Arc (C-shape) ────────────────────────────────────────────────────────
  // Open on one side; good for peek shots from the curve.
  [
    { dx: -200, dy:    0, large: true },
    { dx: -155, dy: -165, large: true },
    { dx: -155, dy:  165, large: true },
  ],

  // ── Chevron ──────────────────────────────────────────────────────────────
  // V-shape. Two flanking boulders funnel enemies toward the tip, creating
  // a crossfire killzone.
  [
    { dx: -185, dy: -95, large: true },
    { dx:    0, dy:  85, large: true },
    { dx:  185, dy: -95, large: true },
  ],

  // ── Corridor ─────────────────────────────────────────────────────────────
  // Four pillars forming two parallel walls with a ~100 px central passage.
  // Ships can also go around the outside of either wall (flanking route).
  [
    { dx: -180, dy: -98, large: true },
    { dx:  180, dy: -98, large: true },
    { dx: -180, dy:  98, large: true },
    { dx:  180, dy:  98, large: true },
  ],

  // ── Box — two entries (N and S) ──────────────────────────────────────────
  // Six-boulder hex room. North and south entries each ~100 px wide.
  // Corner gaps (~140 px) also passable for small ships; adds risk/reward.
  [
    { dx: -100, dy: -200, large: true },
    { dx:  100, dy: -200, large: true },
    { dx: -220, dy:    0, large: true },
    { dx:  220, dy:    0, large: true },
    { dx: -100, dy:  200, large: true },
    { dx:  100, dy:  200, large: true },
  ],

  // ── Box — one entry (N only) ─────────────────────────────────────────────
  // Dead-end ambush room. South wall sealed with a centre fill asteroid
  // (≈4 px gap at each join — physically impassable).
  [
    { dx: -100, dy: -200, large: true },
    { dx:  100, dy: -200, large: true },
    { dx: -220, dy:    0, large: true },
    { dx:  220, dy:    0, large: true },
    { dx: -100, dy:  200, large: true },
    { dx:    0, dy:  210, large: true }, // seals the south gap
    { dx:  100, dy:  200, large: true },
  ],

  // ── Scatter cluster ──────────────────────────────────────────────────────
  // Loose irregular group: two large + two medium. No clear geometry but
  // tight spacing forces ships to weave. Random rotation makes each unique.
  [
    { dx:    0, dy:    0, large: true  },
    { dx:  185, dy:  105, large: true  },
    { dx:  -95, dy:  185, large: false },
    { dx:  110, dy: -155, large: false },
  ],
];

// ---------------------------------------------------------------------------
// Placement helpers
// ---------------------------------------------------------------------------

function rotate2d(dx: number, dy: number, angle: number): [number, number] {
  const c = Math.cos(angle);
  const s = Math.sin(angle);
  return [dx * c - dy * s, dx * s + dy * c];
}

// ---------------------------------------------------------------------------
// Main generation function
// ---------------------------------------------------------------------------

/**
 * Generate a deterministic list of in-arena obstacles from an ArenaConfig.
 *
 * Both the server (physics) and client (rendering) call this with the same
 * config and receive identical results — no schema overhead for static data.
 *
 * Output order: formation clusters first, then individual scattered obstacles.
 */
export function generateObstacles(config: ArenaConfig): ArenaObstacle[] {
  // Scale: density 0.25 → ~30 obstacles, 0.5 → ~60, 1.0 → ~120
  const totalCount = Math.round(config.obstacleDensity * 120);
  if (totalCount <= 0) return [];

  // XOR-shift 32-bit RNG (same algorithm as the border ring)
  let seed = config.seed >>> 0;
  const rand = (): number => {
    seed = (Math.imul(seed ^ (seed >>> 17), 0x45d9f3b)) >>> 0;
    seed = (Math.imul(seed ^ (seed >>> 13), 0x45d9f3b)) >>> 0;
    seed = (seed ^ (seed >>> 16)) >>> 0;
    return seed / 0x100000000;
  };

  const pickSpec = (): [string, string, number, number] => {
    let r = rand() * TOTAL_WEIGHT;
    for (let i = 0; i < WEIGHTS.length; i++) {
      r -= WEIGHTS[i];
      if (r <= 0) return OBSTACLE_SPECS[i];
    }
    return OBSTACLE_SPECS[0];
  };

  const margin = BORDER_THICKNESS + 120;
  const minX = margin;
  const maxX = WORLD_WIDTH  - margin;
  const minY = margin;
  const maxY = WORLD_HEIGHT - margin;

  const placed: ArenaObstacle[] = [];
  let slotsUsed = 0;

  // ── 1. Place formation clusters ─────────────────────────────────────────
  const formationTarget = Math.max(1, Math.round(config.obstacleDensity * 8));

  for (let f = 0; f < formationTarget && slotsUsed < totalCount; f++) {
    const template = FORMATION_TEMPLATES[Math.floor(rand() * FORMATION_TEMPLATES.length)];
    if (slotsUsed + template.length > totalCount) continue;

    // Random centre position and rotation angle
    const angle = rand() * Math.PI * 2;

    // Compute rotated bounding half-extent so we keep the formation in bounds
    const maxDist = Math.max(...template.map(s => Math.hypot(s.dx, s.dy))) + LARGE_RADIUS;

    let placed_formation = false;

    for (let attempt = 0; attempt < 40; attempt++) {
      const cx = minX + maxDist + rand() * (maxX - minX - maxDist * 2);
      const cy = minY + maxDist + rand() * (maxY - minY - maxDist * 2);

      // Check every slot in the template
      const slots: ArenaObstacle[] = [];
      let fits = true;

      for (const slot of template) {
        const [rdx, rdy] = rotate2d(slot.dx, slot.dy, angle);
        const x = cx + rdx;
        const y = cy + rdy;
        const radius = slot.large ? LARGE_RADIUS : MED_RADIUS;

        // Bounds
        if (x < minX || x > maxX || y < minY || y > maxY) { fits = false; break; }

        // Overlap against already-placed obstacles (not same-formation slots —
        // their internal spacing is intentional and defines the passages)
        for (const other of placed) {
          if (Math.hypot(x - other.x, y - other.y) < radius + other.radius + MIN_GAP) {
            fits = false; break;
          }
        }
        if (!fits) break;

        const frames = slot.large ? LARGE_FRAMES : MED_FRAMES;
        slots.push({
          x, y, radius,
          texture:  'sheet',
          frame:    frames[Math.floor(rand() * frames.length)],
          scale:    1.0,
          rotation: rand() * Math.PI * 2,
        });
      }

      if (fits) {
        for (const s of slots) placed.push(s);
        slotsUsed += template.length;
        placed_formation = true;
        break;
      }
    }

    if (!placed_formation) {
      // Consume some RNG so the sequence stays stable regardless of failures
      rand(); rand();
    }
  }

  // ── 2. Fill remaining budget with stratified scatter ────────────────────
  //
  // Divide the playfield into a GRID_COLS × GRID_ROWS grid and assign each
  // scatter obstacle to a cell in round-robin order.  This guarantees even
  // coverage across the whole arena regardless of the RNG seed — no more
  // persistently empty quadrants.
  const GRID_COLS = 4;
  const GRID_ROWS = 4;
  const cellW = (maxX - minX) / GRID_COLS;
  const cellH = (maxY - minY) / GRID_ROWS;

  // Build a shuffled cell order so formations and scatter don't always fill
  // the same cells first.
  const cellOrder: number[] = [];
  for (let c = 0; c < GRID_COLS * GRID_ROWS; c++) cellOrder.push(c);
  // Fisher-Yates shuffle using the seeded RNG
  for (let i = cellOrder.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [cellOrder[i], cellOrder[j]] = [cellOrder[j], cellOrder[i]];
  }

  const remaining = totalCount - slotsUsed;

  for (let i = 0; i < remaining; i++) {
    const [texture, frame, scale, radius] = pickSpec();
    const rotation = rand() * Math.PI * 2;

    // Pick cell in round-robin over the shuffled order
    const cell   = cellOrder[i % cellOrder.length];
    const col    = cell % GRID_COLS;
    const row    = Math.floor(cell / GRID_COLS);
    const cellX0 = minX + col * cellW;
    const cellY0 = minY + row * cellH;

    let x = 0, y = 0, ok = false;

    for (let attempt = 0; attempt < 60; attempt++) {
      x = cellX0 + rand() * cellW;
      y = cellY0 + rand() * cellH;
      // Keep within overall bounds (cells already inset by margin)
      if (x < minX || x > maxX || y < minY || y > maxY) continue;
      ok = placed.every((other) =>
        Math.hypot(x - other.x, y - other.y) >= radius + other.radius + MIN_GAP,
      );
      if (ok) break;
    }

    if (!ok) continue;

    placed.push({ x, y, radius, texture, frame, scale, rotation });
  }

  return placed;
}
